import {uniqBy} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  STANDALONE_CATEGORY_ID,
  STANDALONE_CATEGORY_LABEL,
} from '../../../../../hooks/useFilterCategorySelection';
import type {EagColumn} from '../../../../../types/EagColumn';
import type {EagColumnGroup, EagColumnOrGroup} from '../../../../../types/EagColumnGroup';
import {isEagColumnGroup} from '../../../../../utils/isEagColumnGroup';
import {matchesColumnSearch} from '../../../../../utils/matchesColumnSearch';
import {normalizeSearchText} from '../../../../../utils/normalizeSearchText';

type ResolvedCategory = {
  /** Section label the search matches against; undefined when the groupId is unknown. */
  label: string | undefined;
  columns: EagColumn[];
};

const isFilterable = (col: EagColumn): boolean => isNotNil(col.filter) && col.lockedHidden !== true;

/** Top-level non-group columns with a filter, under STANDALONE_CATEGORY_LABEL. */
const resolveStandaloneCategory = (columnConfig: EagColumnOrGroup[]): ResolvedCategory => ({
  label: STANDALONE_CATEGORY_LABEL,
  columns: columnConfig.filter(
    (item): item is EagColumn => !isEagColumnGroup(item) && isFilterable(item)
  ),
});

/**
 * Filterable children of every group instance with the given groupId, deduped
 * by key; the label comes from the first instance (the rule
 * `buildCategoryItems` uses too).
 */
const resolveGroupCategory = (
  columnConfig: EagColumnOrGroup[],
  groupId: string
): ResolvedCategory => {
  const instances = columnConfig.filter(
    (item): item is EagColumnGroup => isEagColumnGroup(item) && item.groupId === groupId
  );
  return {
    label: instances[0]?.headerName,
    columns: uniqBy(
      (col: EagColumn) => col.key,
      instances.flatMap((group) => group.children.filter(isFilterable))
    ),
  };
};

/**
 * Resolves the list of filterable columns for the selected category.
 *
 * Category resolution:
 * - null → []
 * - '__standalone__' → top-level non-group columns with filter defined
 * - groupId → children of all group instances with matching groupId, deduped by key
 *
 * When searchTerm is non-empty, a column stays when the term matches its
 * headerName OR the category's label (case- and diacritics-insensitive), so
 * searching for a section name — "Otomoto" — surfaces every filter of that
 * section instead of an empty body.
 */
export const getVisibleFilterColumns = (
  columnConfig: EagColumnOrGroup[],
  selectedCategoryId: string | null,
  searchTerm: string
): EagColumn[] => {
  if (!selectedCategoryId) {
    return [];
  }

  const category =
    selectedCategoryId === STANDALONE_CATEGORY_ID
      ? resolveStandaloneCategory(columnConfig)
      : resolveGroupCategory(columnConfig, selectedCategoryId);

  const normalizedTerm = normalizeSearchText(searchTerm);
  if (!normalizedTerm) {
    return category.columns;
  }

  return category.columns.filter((col) =>
    matchesColumnSearch(col.headerName, category.label, normalizedTerm)
  );
};
